import _ from 'lodash';
import type {
  DynamicFields,
  GetPrimeXQuoteDetailsResponse,
} from '../../../../../../api/quotes.api.types';
import type { CoverageEndorsementsState } from '../../../../policies/prime-x/endorsements/types';
import type { QuoteActions } from '../../../../primeX-underwriter/components/footer/constants';
import type { CustomizeQuoteState } from '../../views/customize-quote/types';
import type { EditQuoteState } from '../../views/edit-quote/types';
import type { UnderwriteQuoteState } from '../../views/underwriter/types';
import { generateFieldValues } from '../rater-x/helpers';

/**
 * Builds the base payload for adding a quote to the cart, editing a quote, and underwriting a quote
 * @param quoteState The quote state object.
 * @returns The base payload object.
 */
export function basePayloadBuilder(
  quoteState: QuoteState & { quote?: GetPrimeXQuoteDetailsResponse }
) {
  return {
    effectiveDate: quoteState.effectiveDate,
    endDate: quoteState.endDate,
    ...(_shouldSendPolicyValue(quoteState)
      ? {
          policyValue: quoteState.global.value,
        }
      : {
          aggregateLimit: Number(quoteState.global.value.props.limit?.value),
          deductible: Number(quoteState.global.value.props.deductible?.value),
          retroactivePeriod:
            quoteState.global.value.props.retroactive?.props?.period?.value,
          retroactiveDate:
            quoteState.global.value.props.retroactive?.props?.date?.value,
          waitingPeriod: Number(
            quoteState.global.value.props.waitingPeriod?.value
          ),
        }),
    ...(quoteState.componentGlobals && {
      components: componentFieldsBuilder(quoteState),
    }),
    fieldValues: dynamicFieldsBuilder(quoteState),
    ...(quoteState.quoteCommissionRate != null && {
      commissionRate: quoteState.quoteCommissionRate,
    }),
    isRenewal: (quoteState as CustomizeQuoteState).isRenewal,
    carrierPrefix: quoteState.raterX.carrierPrefix,
    treatyId: quoteState.raterX.treatyId,
  };
}

/**
 * Builds dynamic fields based on the quote state.
 * @param quoteState The quote state containing field values, coverage state, and rating options.
 * @returns An object representing the reduced field values.
 */
export function dynamicFieldsBuilder(quoteState: QuoteState) {
  const componentTypes = new Set(
    Object.keys(quoteState.componentGlobals || {})
  );

  const rootCoverages = _toRecordFilteredByRoot(
    componentTypes,
    quoteState.coverageState
  );

  const rootRatingOptions = _toRecordFilteredByRoot(
    componentTypes,
    quoteState.ratingOptions
  );

  return generateFieldValues(
    quoteState.fieldValues,
    rootCoverages,
    rootRatingOptions
  );
}

export function _toRecordFilteredByRoot<
  T extends { key: string; productType?: string }
>(componentTypes: Set<string>, items: Record<string, T>): Record<string, T> {
  const result: Record<string, T> = {};

  for (const item of Object.values(items)) {
    if (!componentTypes.has(item.productType ?? '')) {
      result[item.key] = item;
    }
  }

  return result;
}

export function componentFieldsBuilder(quoteState: QuoteState) {
  const groupedCoverages: Record<string, any> = _.groupBy(
    quoteState.coverageState,
    'productType'
  );
  const groupedRatingOptions: Record<string, any> = _.groupBy(
    quoteState.ratingOptions,
    'productType'
  );

  return Object.entries(quoteState.componentGlobals || {}).reduce(
    (acc, [key, value]) => {
      return {
        ...acc,
        [key]: {
          policyValue: value.value,
          fieldValues: generateFieldValues(
            [],
            groupedCoverages[key],
            groupedRatingOptions[key]
          ),
        },
      };
    },
    {}
  );
}

export function underwriteQuotePayloadBuilder(
  quoteState: UnderwriteQuoteState,
  quoteAction: QuoteActions,
  isNotificationEnabled = false
) {
  const carrierOptions = quoteState.raterOptions.carrier.options;
  const selectedCarrier = carrierOptions.find(
    (option) => option.treatyId === quoteState.raterOptions.carrier.value
  );
  // Explicit UW intent: the picked carrier is not the quote's assigned one. The
  // backend suspends only the revenue gate for a persisted override (FPX-7027).
  const carrierOverridden =
    selectedCarrier != null && !selectedCarrier.isAssigned;

  return {
    ...basePayloadBuilder(quoteState),
    ...agencyDataBuilder(quoteState),
    ...(quoteState.carrierPrefix && {
      carrierPrefix: quoteState.carrierPrefix,
    }),
    ...(quoteState.treatyId && {
      treatyId: quoteState.treatyId,
    }),
    quoteAction,
    treatyId: quoteState.raterOptions.carrier.value,
    carrierOverridden,
    ...(quoteState.categoryCustomizations && {
      ...quoteState.categoryCustomizations,
    }),
    isNotificationEnabled,
    additionalNamedInsureds: quoteState.account.additionalNamedInsured,
  };
}

const presentNumber = (value: unknown) => {
  const number = Number(value);
  return value == null || Number.isNaN(number) ? undefined : number;
};

/**
 * The terms an underwriter BIND will send, read from the same payload so the bind drawer's
 * review shows what is about to be bound rather than the quote as last saved. Premium is the
 * re-rated total the screen shows; it is unset until the underwriter re-rates. Excess and
 * multi-component quotes send a tower instead of one limit and retention, so those two
 * stay unset for them.
 */
export function pendingBindTerms(quoteState: UnderwriteQuoteState) {
  const payload = basePayloadBuilder(quoteState) as Partial<
    Record<'aggregateLimit' | 'deductible', number>
  > & { effectiveDate?: string | number; endDate?: string | number };
  return {
    aggregateLimit: presentNumber(payload.aggregateLimit),
    retention: presentNumber(payload.deductible),
    premium: presentNumber(quoteState.totalPremium),
    effectiveDate: payload.effectiveDate,
    endDate: payload.endDate,
  };
}

function agencyDataBuilder(quoteState: UnderwriteQuoteState) {
  return {
    agencyData: {
      agencyName: quoteState.agencyName,
      commissionRate: quoteState.commissionRate,
      isAgencyBilled:
        quoteState.categoryCustomizations.rateModifierCategoriesCustomizations
          ?.surplus_category?.select ?? quoteState.isAgencyBilled,
      marketType: quoteState.marketType,
    },
  };
}

export function reduceFieldValues(
  acc: DynamicFields,
  field: Record<string, any>
) {
  if (field.isSelected === false) {
    return acc;
  }

  return {
    ...acc,
    [field.fieldId]: field.value ?? null,
  };
}

export function reduceDynamicFieldSettings(
  acc: Record<string, { isPegValue: boolean }>,
  field: {
    fieldId: string;
    isSelected?: boolean;
    isPegValue?: boolean;
  }
) {
  if (field.isSelected === false) {
    return acc;
  }

  return {
    ...acc,
    [field.fieldId]: {
      isPegValue: field.isPegValue === true,
    },
  };
}

function _shouldSendPolicyValue(
  quoteState: QuoteState & { quote?: GetPrimeXQuoteDetailsResponse }
) {
  return (
    (quoteState.quote?.globalValue &&
      !_.isEmpty(quoteState.quote.globalValue)) ||
    (quoteState.raterX.components && !_.isEmpty(quoteState.raterX.components))
  );
}

type QuoteState =
  | CustomizeQuoteState
  | EditQuoteState
  | UnderwriteQuoteState
  | CoverageEndorsementsState;
